import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useFeature } from '@treams/treams-hooks';
import { DeleteIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	GraphQLErrorMessage,
	Heading3,
	IconButton,
	Paragraph,
	QuillInput,
	Select,
	style as fieldStyle,
	tokens,
} from '@treams/treams-ui';

import DetailsTableForm from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import MomentTemplateEditorTypeSettings from './MomentTemplateEditorTypeSettings';

const Label = styled(Box)`
	font-size: 16px;
	color: ${tokens.text.tertiary};
`;

const Cell = styled(Box)`
	color: ${tokens.text.secondary};
`;

const InputElement = styled.input`
	${fieldStyle}
`;

function Notification({
	name,
	onRemove,
	momentDuration,
}: {
	name: string;
	url: string;
	onRemove: () => void;
	momentDuration: string;
}) {
	const { t } = useTranslation();

	const [{ value: offset }, , { setValue: setOffset }] = useField<number>(`${name}.offset`);
	const [{ value: duration }] = useField<number>(momentDuration);
	const [{ value: type }, , { setValue: setType }] = useField<graphql.MomentNotificationType>(
		`${name}.type`,
	);

	const limitOffset = (offsetType: graphql.MomentNotificationType, offsetNumber: number) => {
		let max = 365;

		if (offsetType === graphql.MomentNotificationType.BeforeEnd) {
			max = duration - 2;
		}

		setOffset(Math.max(0, Math.min(max, offsetNumber)));
	};

	return (
		<Flex alignItems="center" pt={2}>
			<Cell mr={3}>
				<InputElement
					type="number"
					min={0}
					max={365}
					step={1}
					small
					value={offset}
					onChange={(event) => {
						limitOffset(type, parseInt(event.target.value, 10));
					}}
				/>
			</Cell>
			<Cell mr={5}>{t('common.settings.momentTemplates.edit.reminders.days')}</Cell>
			<Cell mr={3} width={6 / 12}>
				<Select
					small
					selectedValue={type}
					options={Object.values(graphql.MomentNotificationType).map((notificationType) => ({
						title: t(
							`common.settings.momentTemplates.edit.reminders.${notificationType.toLowerCase()}`,
						),
						value: notificationType,
					}))}
					onChange={(value) => {
						if (value) {
							setType(value as graphql.MomentNotificationType);
							limitOffset(value as graphql.MomentNotificationType, offset);
						}
					}}
				/>
			</Cell>
			<IconButton icon={<DeleteIcon />} onClick={onRemove} type="destructive" />
		</Flex>
	);
}

const Hint = styled(Flex)`
	background: ${tokens.surface.secondary};
	padding: 10px;
	border-radius: 4px;
	margin-top: 15px;
	align-items: center;
`;

const HintIcon = styled(Box)`
	color: ${tokens.text.secondary};
	flex-shrink: 0;
	flex-grow: 0;
	text-transform: uppercase;
	margin-right: 10px;
	font-weight: bold;
	font-size: 12px;
`;

const HintMessage = styled(Box)`
	color: ${tokens.text.secondary};
	flex-shrink: 1;
	flex-grow: 1;
`;

export default function MomentTemplateEditor({
	url,
	name,
}: {
	url: string;
	returnURL: string;
	name: string;
}) {
	const { t } = useTranslation();

	const hasFeedbackFixedCycleRelease = useFeature(graphql.FeatureName.FeedbackFixedCycleRelease);

	const [notifications, , { setValue: setNotificationsValue }] = useField<
		graphql.MomentNotification[]
	>(`${name}.notifications`);

	const [{ value: momentTemplate }] = useField<graphql.MomentTemplateInput>(name);

	const error = undefined;

	const showDuration = momentTemplate.type !== graphql.MomentType.Message;

	return (
		<Flex flexDirection="column">
			<Box mb={3}>
				<Heading3 mb={0}>{t('common.settings.momentTemplates.edit.details')}</Heading3>
			</Box>
			<GraphQLErrorMessage error={error} showValidationDetails />
			<DetailsTableForm
				formValues={[
					{
						label: t('common.settings.momentTemplates.edit.name'),
						name: `${name}.title`,
						type: DetailsTableFormValueTypes.Multilingual,
					},
					{
						label: t('common.settings.momentTemplates.edit.when'),
						name: `${name}.startOffset`,
						type: DetailsTableFormValueTypes.Number,
					},
					...(showDuration
						? [
								{
									label: t('common.settings.momentTemplates.edit.duration'),
									name: `${name}.duration`,
									type: DetailsTableFormValueTypes.Number,
								},
							]
						: []),
					...(momentTemplate.type === graphql.MomentType.Feedback && hasFeedbackFixedCycleRelease
						? [
								{
									label: t('common.settings.momentTemplates.edit.feedbackReleaseDate'),
									name: `${name}.feedback.releaseDate`,
									type: DetailsTableFormValueTypes.Date,
								},
							]
						: []),
				]}
			/>
			<MomentTemplateEditorTypeSettings name={name} type={momentTemplate.type} />
			{momentTemplate.type !== graphql.MomentType.Message && (
				<>
					<Label mt={5} mb={1}>
						{t('common.settings.momentTemplates.edit.guide')}
					</Label>
					<Box mb={3}>
						<Paragraph>{t('common.settings.momentTemplates.edit.guideDescription')}</Paragraph>
					</Box>
					<QuillInput.Localizable.Formik name={`${name}.description`} />
				</>
			)}
			<Box mt={3}>
				<Label mt={5} mb={1}>
					{t('common.settings.momentTemplates.edit.instructionLabel')}
				</Label>
				<Box mb={3}>
					<Paragraph>{t('common.settings.momentTemplates.edit.instruction')}</Paragraph>
				</Box>
				<QuillInput.Localizable.Formik name={`${name}.instruction`} />
			</Box>
			{momentTemplate.type !== graphql.MomentType.Message && (
				<>
					<Label mt={5}>{t('common.settings.momentTemplates.edit.reminders.title')}</Label>
					<Box mb={3}>
						{notifications.value.map((notification, index) => (
							<Notification
								key={notification.id}
								name={`${name}.notifications[${index}]`}
								url={`${url}/notifications/${notification.id}`}
								momentDuration={`${name}.duration`}
								onRemove={() => {
									const updatedNotifications = notifications.value.slice();
									updatedNotifications.splice(index, 1);
									setNotificationsValue(updatedNotifications);
								}}
							/>
						))}
					</Box>
					<Flex>
						<Button
							title={t('common.settings.momentTemplates.edit.addNotification')}
							onClick={() => {
								const id = `_${Math.random().toString()}`;

								setNotificationsValue(
									notifications.value.concat([
										{
											id,
											type: graphql.MomentNotificationType.BeforeStart,
											offset: 0,
										},
									]),
								);
							}}
							type="secondary"
							small
						/>
					</Flex>
					<Hint>
						<HintIcon>{t('common.hint.title')}</HintIcon>
						<HintMessage>{t('common.settings.momentTemplates.hint.notifications')}</HintMessage>
					</Hint>
				</>
			)}
		</Flex>
	);
}
